import React, { useEffect, useMemo, useState } from 'react'
import styles from './NotificationPopup.module.css'
import { useTranslation } from '@amusnet/translations/react'
import { cn, getZoomToFit } from '@amusnet/react-ui'
import { useConfigurationStore } from '@stores'
import {
    AutostartFeature,
    GameUIMessageType,
    isAutostartFeature,
    FeatureAutostartCounterEndedMessage,
} from 'game-ui-types'

interface NotificationPopupProps {
    shouldShow: boolean
    duration?: number
    children?: React.ReactNode
    type?: NotificationType
}

type NotificationType = 'error' | AutostartFeature

const NotificationPopup: React.FC<NotificationPopupProps> = ({
    shouldShow = false,
    duration = 5000,
    children,
    type,
}) => {
    const { t } = useTranslation()
    const notificationPopupFreespinsCounterWrapperPosition = useConfigurationStore(
        (state) => state.getComponentConfig('NotificationPopupFreespinsCounterWrapper').position
    )

    const notificationPopupFreespinsCounterWrapperStyles = useConfigurationStore(
        (state) => state.getComponentConfig('NotificationPopupFreespinsCounterWrapper').styles
    )

    const [visible, setVisible] = useState(false)
    const [remainingMs, setRemainingMs] = useState(duration)

    const timeLeftSec = Math.ceil(remainingMs / 1000)
    const progressWidth = `${(remainingMs / duration) * 100}%`

    const sendFeatureAutoStartCounterEndedMessage = (feature: AutostartFeature) => {
        const msg: FeatureAutostartCounterEndedMessage = {
            type: GameUIMessageType.FEATURE_AUTOSTART_COUNTER_ENDED,
            payload: {
                feature,
            },
        }

        window.uiBus.postMessage(msg)
    }

    useEffect(() => {
        if (!shouldShow) return

        setVisible(true)

        const start = Date.now()
        let raf: number

        const tick = () => {
            const timePassed = Date.now() - start
            const remaining = Math.max(duration - timePassed, 0)

            setRemainingMs(remaining)

            if (remaining > 0) {
                raf = requestAnimationFrame(tick)
            } else {
                setVisible(false)

                if (isAutostartFeature(type)) {
                    sendFeatureAutoStartCounterEndedMessage(type)
                }
            }
        }

        raf = requestAnimationFrame(tick)

        return () => cancelAnimationFrame(raf)
    }, [shouldShow, duration, type])

    const renderContent = useMemo(() => {
        if (isAutostartFeature(type)) {
            const autoStartText = t('gameui.thisFeatureWillStartAutomaticallyAfter')
            const secondsShortText = t('gameui.secondsShort')
            const singleLineText = `${autoStartText} 15 ${secondsShortText}.`

            return (
                <div
                    className={cn(
                        'relative flex flex-col items-center overflow-hidden bg-[#1E2024]/60 backdrop-blur-[15px]',
                        notificationPopupFreespinsCounterWrapperStyles
                    )}
                    style={{
                        ...notificationPopupFreespinsCounterWrapperPosition,
                    }}
                >
                    <div className="pt-[10px] w-full text-center text-white">
                        <div
                            className="inline-flex items-baseline justify-center gap-[10px] whitespace-nowrap font-normal text-[30px] leading-[40px] -mt-[4px]"
                            style={{ zoom: getZoomToFit(37, singleLineText) }}
                        >
                            <span>{autoStartText}</span>
                            <span className="font-bold text-[40px] leading-[40px]">{timeLeftSec}</span>
                            <span>{secondsShortText}.</span>
                        </div>
                    </div>

                    <div className="mt-auto w-full">
                        <div className="w-full h-[8px] bg-[#303031] overflow-hidden">
                            <div
                                className="h-full bg-[#A6CE39]"
                                style={{
                                    width: progressWidth,
                                }}
                            />
                        </div>
                    </div>
                </div>
            )
        }

        switch (type) {
            case 'error':
                return (
                    <div
                        style={{
                            background:
                                'linear-gradient(#1E2024,#1E2024) padding-box, linear-gradient(180deg,#F9C246 0%,#F9E746 47.92%,#F9C246 100%) border-box',
                            border: '3px solid transparent',
                            boxShadow: '0 10px 30px rgba(0,0,0,0.4)',
                            width: '800px',
                            height: '140px',
                            borderRadius: '16px',
                            color: 'white',
                        }}
                    >
                        {children}
                    </div>
                )
            default:
                return <div className={styles.defaultContent}>{children}</div>
        }
    }, [type, visible, timeLeftSec, progressWidth])

    return <div className={cn(styles.popup, visible && styles.show)}>{renderContent}</div>
}

export default NotificationPopup
